<template>
  <div class="edit-page-form">
    <form action="#" @submit.prevent="">
      <TextInput
        :id="currentPageForm.title.id"
        :label="currentPageForm.title.label"
        v-model="currentPageForm.title.binding"
        :error="currentPageForm.title.error"
        :rules="currentPageForm.title.rules"
        @validate="onValidate('title', $event)"
      />
      <div class="edit-page-form__actions">
        <Btn
          type="submit"
          text="Save!"
          :loading="isLoading"
          :disabled="isDeleting"
          @click.prevent="handleSubmit"
          variant="secondary"
        />
        <Btn
          text="Delete"
          @click="deletePage"
          variant="danger"
          :loading="isDeleting"
          :disabled="isLoading"
        />
      </div>
    </form>
  </div>
</template>

<script>
  import { ref, watch } from 'vue';
  import { useFormStore } from '@/store/formStore';
  import { useToastStore } from '@/store/toastStore';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import { validateInput } from '@/utils/validation';

  export default {
    name: 'EndingPageSettingsModalForm',
    components: {
      TextInput,
      Btn,
    },
    props: {
      pageIndex: {
        type: Object,
        required: true,
      },
      onSubmit: {
        type: Function,
        required: true,
      },
      onError: {
        type: Function,
        required: true,
      },
    },
    emits: ['close'],
    setup(props, { emit }) {
      const formStore = useFormStore();
      const toastStore = useToastStore();
      const currentPageForm = ref(null);
      const isLoading = ref(false);
      const isDeleting = ref(false);

      watch(
        () => props.pageIndex,
        (newIndex) => {
          if (newIndex !== null && formStore.currentForm.ending_pages[newIndex]) {
            currentPageForm.value = {
              title: {
                binding: formStore.currentForm.ending_pages[newIndex].title,
                label: 'Page title',
                error: '',
                rules: ['required', 'maxLength'],
              },
            };
          }
        },
        { immediate: true }
      );

      const onValidate = (key, errorMessage) => {
        currentPageForm.value[key].error = errorMessage;
      };

      const validateForm = () => {
        Object.keys(currentPageForm.value).forEach((key) => {
          const input = currentPageForm.value[key];
          input.error = validateInput(input.binding, input.rules);
        });
        return Object.values(currentPageForm.value).every((input) => !input.error);
      };

      const handleSubmit = async () => {
        isLoading.value = true;
        const allFieldsValid = validateForm();

        if (allFieldsValid) {
          const formData = Object.keys(currentPageForm.value).reduce((acc, key) => {
            acc[key] = currentPageForm.value[key].binding;
            return acc;
          }, {});
          saveChanges(formData);
        } else {
          const errors = Object.keys(currentPageForm.value).reduce((acc, key) => {
            if (currentPageForm.value[key].error) {
              acc[key] = currentPageForm.value[key].error;
            }
            isLoading.value = false;
            return acc;
          }, {});
          console.log(errors);
          props.onError(errors);
        }
      };

      const closeModal = () => {
        emit('close');
      };

      // Actions to set the changes in the form store and then submit the form
      const saveChanges = async (data) => {
        try {
          await formStore.updateEndingPage(props.pageIndex, data);
          props.onSubmit();
        } catch (error) {
          console.error('Failed to save changes:', error);
          alert('Failed to save changes');
          isLoading.value = false;
        }
      };

      const deletePage = async () => {
        isDeleting.value = true;
        try {
          const result = await formStore.removeEndingPage(props.pageIndex);
          if (result.status === 'error') {
            toastStore.addToast({
              message: result.message,
              type: 'error',
              duration: 10000,
            });
            isDeleting.value = false;
            return;
          }
          props.onSubmit();
        } catch (error) {
          console.error('Failed to delete ending page:', error);
          toastStore.addToast({
            message: 'Failed to delete ending page',
            type: 'error',
            duration: 3000,
          });
          isDeleting.value = false;
        }
      };

      return {
        currentPageForm,
        onValidate,
        closeModal,
        saveChanges,
        deletePage,
        handleSubmit,
        isLoading,
        isDeleting,
      };
    },
  };
</script>

<style scoped lang="scss">
  .edit-page-form {
    &__actions {
      margin-top: 1rem;
      display: flex;
      justify-content: space-between;
    }
  }
</style>
